import { doc, getDoc, updateDoc, setDoc, arrayUnion } from "firebase/firestore"; // Firebase Firestore methods
import { db } from "../../../firebase"; // Your Firebase configuration
import { v4 as uuidv4 } from "uuid"; // For generating unique IDs
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../firebase";

/**
 * Logs an action in the user's audit trail using cloud function (bypasses permissions).
 * @param {string} currentUserEmail - The email of the current user performing the action.
 * @param {string} system - The system the user is working in (e.g., "Users").
 * @param {string} action - The action taken by the user (e.g., "Manage Record").
 * @param {string} target - The target of the action (e.g., the selected user's email or record ID).
 * @param {string} timestamp - The timestamp of the action.
 */
const logUserActionCloud = async (
  currentUserEmail,
  system,
  action,
  target,
  timestamp
) => {
  const uniqueId = uuidv4();
  const auditEntry = {
    system,
    action,
    target,
    timestamp,
    uniqueId,
  };

  try {
    // Use cloud function to log audit trail
    const logAuditAction = httpsCallable(functions, "logAuditAction");
    
    await logAuditAction({
      userEmail: currentUserEmail,
      auditEntry,
      auth: true,
    });
    
    console.log("Audit trail updated successfully via cloud function.");
  } catch (error) {
    console.error("Error updating audit trail via cloud function:", error);
    throw error;
  }
};

/**
 * Logs an action in the user's audit trail (original function with permission handling).
 * @param {string} currentUserEmail - The email of the current user performing the action.
 * @param {string} system - The system the user is working in (e.g., "Users").
 * @param {string} action - The action taken by the user (e.g., "Manage Record").
 * @param {string} target - The target of the action (e.g., the selected user's email or record ID).
 * @param {string} timestamp - The timestamp of the action.
 */
const logUserAction = async (
  currentUserEmail,
  system,
  action,
  target,
  timestamp
) => {
  const uniqueId = uuidv4(); // Generate a unique ID for this action
  const auditEntry = {
    system,
    action,
    target,
    timestamp,
    uniqueId,
  };

  try {
    // Reference to the current user's document in Firestore
    const userDocRef = doc(db, "NSSA-Group/Shared/usersHero", currentUserEmail);

    // Get the user's document
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      // Check if the auditTrail field exists in the document
      if (userDoc.data().auditTrail) {
        // Append to existing auditTrail array
        await updateDoc(userDocRef, {
          auditTrail: arrayUnion(auditEntry), // Add the new audit entry to the array
        });
      } else {
        // If auditTrail doesn't exist, create it and add the entry
        await updateDoc(userDocRef, {
          auditTrail: [auditEntry], // Create the auditTrail array and add the first entry
        });
      }
      console.log("Audit trail updated successfully.");
    } else {
      // If the document doesn't exist, create it with the auditTrail array
      await setDoc(userDocRef, {
        auditTrail: [auditEntry],
        createdAt: timestamp, // Add a timestamp for when the document was created
      });
      console.log("User document created with initial audit trail entry.");
    }
  } catch (error) {
    // Silently handle audit trail errors - they shouldn't break the main operation
    console.warn("Audit trail update failed (non-critical):", error.message);
    // Try cloud function as fallback
    try {
      await logUserActionCloud(currentUserEmail, system, action, target, timestamp);
    } catch (cloudError) {
      console.warn("Cloud function audit trail also failed (non-critical):", cloudError.message);
      // Don't throw - audit trail failure shouldn't break the main operation
    }
  }
};

export default logUserAction;
